/**
 * Aviso de cookies de MineraliA.
 *
 * Usa la API de privacidad de Shopify (consent-tracking-api) para leer y guardar el
 * consentimiento del visitante. Se muestra sólo si aún no hay una decisión.
 * Al decidir (o si ya había una decisión) emite `mineralia:consent-decided` en `document`,
 * que otros componentes (p. ej. el popup del newsletter) usan para no encimarse.
 */

const STORAGE_KEY = 'mineralia-cookie-consent';
const DECIDED_EVENT = 'mineralia:consent-decided';
const API_TIMEOUT = 5000;

function readStoredDecision() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function storeDecision(accepted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted, at: Date.now() }));
  } catch (error) {
    // Almacenamiento no disponible (modo privado estricto): la API de Shopify sigue siendo la fuente principal.
  }
}

function loadPrivacyApi() {
  return new Promise((resolve) => {
    const shopify = window.Shopify;
    let settled = false;
    const finish = (api) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(api || null);
    };
    const timer = setTimeout(() => finish(window.Shopify?.customerPrivacy), API_TIMEOUT);

    if (shopify?.customerPrivacy) {
      finish(shopify.customerPrivacy);
      return;
    }

    if (typeof shopify?.loadFeatures !== 'function') {
      finish(null);
      return;
    }

    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        finish(error ? null : window.Shopify?.customerPrivacy);
      });
    } catch (error) {
      finish(null);
    }
  });
}

class MineraliaCookieConsent extends HTMLElement {
  constructor() {
    super();
    this.privacy = null;
    this.handleClick = this.handleClick.bind(this);
    this.handleSectionSelect = this.handleSectionSelect.bind(this);
    this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
    this.handleExternalConsent = this.handleExternalConsent.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);

    if (this.hasAttribute('data-design-mode')) {
      // En el editor no se bloquea nada: se muestra sólo al seleccionar la sección.
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
      this.markDecided({ accepted: null, initial: true });
      return;
    }

    document.documentElement.dataset.mineraliaConsent = 'checking';
    document.addEventListener('visitorConsentCollected', this.handleExternalConsent);
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('shopify:section:select', this.handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
    document.removeEventListener('visitorConsentCollected', this.handleExternalConsent);
  }

  async init() {
    this.privacyReady = loadPrivacyApi();

    if (readStoredDecision()) {
      this.privacy = await this.privacyReady;
      this.markDecided({ accepted: readStoredDecision()?.accepted ?? null, initial: true });
      return;
    }

    // Sin decisión guardada en este navegador se muestra ya, sin esperar a la API de Shopify
    // (llega tarde y retrasaba el aviso varios segundos). Al cargarla se comprueba si ya había
    // una decisión registrada por otra vía y, en ese caso, el aviso se retira.
    document.documentElement.dataset.mineraliaConsent = 'pending';
    if (!this.standDownForNativeBanner()) this.show();

    this.privacy = await this.privacyReady;
    if (document.documentElement.dataset.mineraliaConsent === 'decided') return;
    if (this.hasDecision()) {
      this.hide();
      this.markDecided({ accepted: null, initial: true });
    }
  }

  /**
   * Si en Admin sigue activo el banner nativo de cookies de Shopify (#shopify-pc__banner), este aviso
   * se retira para no mostrar dos. La decisión del banner nativo llega por `visitorConsentCollected`
   * (handleExternalConsent), así que el resto del sitio (p. ej. el popup de newsletter) sigue igual.
   * @returns {boolean} true si el banner nativo ya está en la página
   */
  standDownForNativeBanner() {
    const nativeBanner = () => document.getElementById('shopify-pc__banner');
    if (nativeBanner()) return true;

    // El banner nativo se inyecta de forma asíncrona: se vigila unos segundos por si aparece después.
    const observer = new MutationObserver(() => {
      if (!nativeBanner()) return;
      observer.disconnect();
      this.hide();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
    return false;
  }

  hasDecision() {
    if (readStoredDecision()) return true;

    const consent = this.privacy?.currentVisitorConsent?.();
    return Boolean(consent && typeof consent.analytics === 'string' && consent.analytics !== '');
  }

  show() {
    this.hidden = false;
    this.classList.add('is-visible');
  }

  hide() {
    this.classList.remove('is-visible');
    this.hidden = true;
  }

  handleClick(event) {
    const button = event.target.closest('[data-consent]');
    if (!button) return;

    const accepted = button.dataset.consent === 'accept';

    if (this.hasAttribute('data-design-mode')) {
      this.hide();
      return;
    }

    this.querySelectorAll('[data-consent]').forEach((item) => (item.disabled = true));
    this.saveConsent(accepted);
  }

  async saveConsent(accepted) {
    // Si el visitante decide antes de que cargue la API, se espera a ella (máx. API_TIMEOUT).
    if (!this.privacy && this.privacyReady) this.privacy = await this.privacyReady;

    const finish = () => {
      storeDecision(accepted);
      this.hide();
      this.markDecided({ accepted, initial: false });
    };

    const api = this.privacy;
    if (typeof api?.setTrackingConsent !== 'function') {
      finish();
      return;
    }

    let done = false;
    const complete = () => {
      if (done) return;
      done = true;
      finish();
    };

    // No se hace esperar al visitante si la API tarda en responder.
    const fallback = setTimeout(complete, 3000);

    try {
      api.setTrackingConsent(
        {
          analytics: accepted,
          marketing: accepted,
          preferences: accepted,
          sale_of_data: accepted,
        },
        () => {
          clearTimeout(fallback);
          complete();
        }
      );
    } catch (error) {
      clearTimeout(fallback);
      complete();
    }
  }

  handleExternalConsent() {
    // Otra interfaz (p. ej. el banner nativo de Shopify) ya registró la decisión.
    if (document.documentElement.dataset.mineraliaConsent === 'decided') return;
    this.hide();
    this.markDecided({ accepted: null, initial: false });
  }

  markDecided(detail) {
    document.documentElement.dataset.mineraliaConsent = 'decided';
    document.dispatchEvent(new CustomEvent(DECIDED_EVENT, { detail }));
  }

  handleSectionSelect(event) {
    if (event.detail?.sectionId === this.dataset.sectionId) this.show();
  }

  handleSectionDeselect(event) {
    if (event.detail?.sectionId === this.dataset.sectionId) this.hide();
  }
}

if (!customElements.get('mineralia-cookie-consent')) {
  customElements.define('mineralia-cookie-consent', MineraliaCookieConsent);
}
